/*
 * Jira Ticket:
 * Created Date: Thu, 201 Nov 2019, 16:57:48 pm
 * Author: Jessica Mowatt
 * Email: jessica.mowatt@thedistance.co.uk
 * Copyright (c) 2019 The Distance
 */

import buildGraphqlProvider, { buildQuery } from 'ra-data-graphql-simple';
import getApolloClient from './apolloClient';
import { __schema as schema } from './schema';
import getResponseParser from './customQueryResponseParser';
// resource name mapping
import resources from './resourceMap';
// custom queries
import overrideQueries from './overrideQueries';
import overrideParams from './overrideParams';
import addCustomQueries from './customQueries';
import decorateResponse from './decorateResponse';

const customBuildQuery = introspection => (type, resource, params) => {
  let queryToSend;

  const overriddenQuery = overrideQueries({
    type,
    resource,
    params,
  });

  const overridenParams = overrideParams({
    type,
    resource,
    params,
  });

  if (overridenParams) {
    const builtQueryWithOverridenParams = buildQuery(introspection)(
      type,
      resource,
      overridenParams
    );
    queryToSend = builtQueryWithOverridenParams;
  } else {
    // Use the default query variables and parseResponse
    queryToSend = buildQuery(introspection)(type, resource, params);
  }
  if (overriddenQuery) {
    return {
      ...queryToSend,
      // Override the query
      query: overriddenQuery,
      parseResponse: response => getResponseParser()(type)(response),
      errorPolicy: 'all',
    };
  }

  return {
    ...queryToSend,
    parseResponse: response => getResponseParser()(type)(response),
    errorPolicy: 'all',
  };
};

const createProvider = async () => {
  const client = await getApolloClient({}); // add options to client: {clientOptions, inMemCacheOptions}
  const publicClient = await getApolloClient({
    httpLinkUri: process.env.REACT_APP_REGISTRATION_URI,
  });
  const gqlProvider = await buildGraphqlProvider({
    client,
    buildQuery: customBuildQuery,
    introspection: { schema },
    errorPolicy: 'all',
    parseResponse: getResponseParser,
  });

  const dataProviders = [
    {
      dataProvider: gqlProvider,
      resources,
    },
  ];

  return async (type, resource, params) => {
    //Override these values because resource name is Subcontractor and Contract
    if (resource === 'Subcontractor' || resource === 'Contract') {
      resource = 'Subcontract';
    }
    const dataProviderMapping = dataProviders.find(
      dp => !dp.resources || dp.resources.hasOwnProperty(resource)
    );

    if (dataProviderMapping && dataProviderMapping.resources) {
      resource = dataProviderMapping.resources[resource];
    }

    const customQueries = await addCustomQueries({
      type,
      resource,
      params,
      client,
      publicClient,
    });

    if (customQueries) {
      return customQueries;
    }

    // const result = await dataProviderMapping.dataProvider(
    //   type,
    //   resource,
    //   params
    // );

    // return decorateResponse({
    //   type,
    //   resource,
    //   result,
    // });
    if (typeof dataProviderMapping.dataProvider === 'object') {
      switch (type) {
        case 'GET_LIST':
          type = 'getList';
          break;
        case 'GET_ONE':
          type = 'getOne';
          break;
        case 'GET_MANY':
          type = 'getMany';
          break;
        case 'GET_MANY_REFERENCE':
          type = 'getManyReference';
          break;
        case 'UPDATE':
          type = 'update';
          break;
        case 'UPDATE_MANY':
          type = 'updateMany';
          break;
        case 'CREATE':
          type = 'create';
          break;
        case 'DELETE':
          type = 'delete';
          break;
        case 'DELETE_MANY':
          type = 'deleteMany';
          break;
        default:
          throw new Error('Unsupported query type');
      }

      const result = await dataProviderMapping.dataProvider[type](
        resource,
        params
      );

      return decorateResponse({
        type,
        resource,
        result,
      });
    }
  };
};

let provider = null;
const getOrCreateProvider = async () => {
  if (!provider) {
    provider = await createProvider();
  }

  return provider;
};

export default getOrCreateProvider;
